import { useEffect, useState } from "react";
import { Phone, Info, Sun, HeartHandshake, X, ZoomIn, Download } from "lucide-react";
import { CHURCH, POSTERS } from "@/data/church";
import { PageHero } from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";

const partitionPosters = () => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const withDate = POSTERS.map((p) => ({ ...p, day: new Date(`${p.date}T00:00:00`) }));
  return {
    upcoming: withDate.filter((p) => p.day >= today).sort((a, b) => a.day - b.day),
    past: withDate.filter((p) => p.day < today).sort((a, b) => b.day - a.day),
  };
};

const PosterCard = ({ poster, index, onOpen, dimmed = false }) => (
  <Reveal delay={(index % 3) * 0.08}>
    <div
      className={`overflow-hidden rounded-2xl border-2 border-[#C59B4B]/50 bg-white shadow-[0_10px_36px_rgba(4,40,38,0.25)] transition-all duration-300 hover:-translate-y-1 hover:border-[#F2C14E] hover:shadow-2xl ${
        dimmed ? "opacity-80 hover:opacity-100" : ""
      }`}
    >
      <button
        data-testid={`poster-card-${poster.id}`}
        onClick={() => onOpen(poster)}
        aria-label={`Enlarge poster: ${poster.medium}, ${poster.dateLabel}`}
        className="group relative block w-full text-left"
      >
        <img
          src={poster.image}
          alt={`Poster for ${poster.dateLabel} with guest medium ${poster.medium}`}
          loading="lazy"
          className="w-full object-cover"
        />
        <span className="absolute bottom-3 right-3 inline-flex items-center gap-2 rounded-full bg-[#A61C4E] px-4 py-2 text-sm font-semibold text-[#FBF6EC] opacity-0 transition-opacity duration-200 group-hover:opacity-100">
          <ZoomIn className="h-4 w-4" aria-hidden="true" />
          Tap to enlarge
        </span>
      </button>
      <div className="bg-[#6B21A8] p-5 text-center">
        <p className="font-serif text-xl font-medium text-[#FBF6EC]">{poster.medium}</p>
        <p className="mt-1 text-base font-medium text-[#F2C14E]">{poster.dateLabel} · {poster.timeLabel}</p>
        <a
          href={poster.image}
          download={`${poster.id}.png`}
          data-testid={`poster-download-${poster.id}`}
          className="mt-4 inline-flex min-h-12 w-full items-center justify-center gap-2 rounded-xl bg-[#C59B4B] px-5 text-base font-semibold text-[#101826] shadow-md transition-all duration-200 hover:bg-[#d4ab5f] hover:shadow-lg active:scale-95"
        >
          <Download className="h-5 w-5" aria-hidden="true" />
          Download Poster
        </a>
      </div>
    </div>
  </Reveal>
);

const Lightbox = ({ poster, onClose }) => {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  if (!poster) return null;

  return (
    <div
      data-testid="poster-lightbox"
      onClick={onClose}
      className="fixed inset-0 z-[100] flex items-center justify-center bg-[#2A0A45]/95 p-4 sm:p-8"
      role="dialog"
      aria-modal="true"
      aria-label={`Poster for ${poster.medium}`}
    >
      <div onClick={(e) => e.stopPropagation()} className="relative max-h-full">
        <img
          src={poster.image}
          alt={`Poster for ${poster.dateLabel} with guest medium ${poster.medium}`}
          className="max-h-[85vh] w-auto max-w-full rounded-xl shadow-2xl"
        />
        <p className="mt-4 text-center font-serif text-xl text-[#FBF6EC]">
          {poster.medium} · {poster.dateLabel} · {poster.timeLabel}
        </p>
        <button
          data-testid="poster-lightbox-close"
          onClick={onClose}
          aria-label="Close enlarged poster"
          className="absolute -top-3 -right-3 inline-flex min-h-12 min-w-12 items-center justify-center rounded-full bg-[#C59B4B] text-[#101826] shadow-lg transition-transform hover:scale-105"
        >
          <X className="h-6 w-6" aria-hidden="true" />
        </button>
      </div>
    </div>
  );
};

export default function Events() {
  const [open, setOpen] = useState(null);
  const { upcoming, past } = partitionPosters();

  return (
    <>
      <PageHero
        testId="events-hero"
        kicker="Posters & Events"
        title="Our Guest Mediums & Services"
        lead="Every Sunday evening at 6:30pm we gather for our Divine Service, with healing available before every service. Everyone is welcome."
      />

      <section data-testid="sunday-service-section" className="bg-gradient-to-b from-[#F7ECD9] to-[#FBF6EC] py-16 sm:py-20">
        <div className="mx-auto max-w-3xl px-5 sm:px-8">
          <Reveal>
            <div className="rounded-3xl border-2 border-[#C59B4B]/70 bg-gradient-to-br from-[#6B21A8] to-[#7C2DAE] p-8 text-center shadow-[0_16px_50px_rgba(88,28,135,0.28)] sm:p-12">
              <Sun className="mx-auto h-8 w-8 text-[#F2C14E]" aria-hidden="true" />
              <h2 className="mt-4 font-serif text-2xl sm:text-3xl font-medium text-[#FBF6EC]">
                Sunday Divine Service
              </h2>
              <p className="mt-4 font-serif text-4xl sm:text-5xl font-semibold text-[#F2C14E]">
                Every Sunday · 6:30pm
              </p>
              <p className="mt-5 flex items-center justify-center gap-2 text-base sm:text-lg font-medium text-[#F3E8FA]">
                <HeartHandshake className="h-5 w-5 text-[#F2C14E]" aria-hidden="true" />
                Healing is available before all Sunday services
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      <section data-testid="upcoming-events-section" className="bg-gradient-to-b from-[#0F766E] to-[#047857] py-16 sm:py-24">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal>
            <h2 className="text-center font-serif text-2xl sm:text-3xl lg:text-4xl font-medium tracking-tight text-[#FBF6EC]">
              Upcoming Events
            </h2>
            <p className="mx-auto mt-4 max-w-2xl text-center text-base sm:text-lg text-[#D9EEE6]">
              Tap any poster to view it in full size.
            </p>
          </Reveal>
          <div className="mt-12 grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
            {upcoming.map((p, i) => (
              <PosterCard key={p.id} poster={p} index={i} onOpen={setOpen} />
            ))}
          </div>

          {past.length > 0 && (
            <>
              <Reveal>
                <h2
                  data-testid="past-events-heading"
                  className="mt-20 text-center font-serif text-2xl sm:text-3xl font-medium tracking-tight text-[#FBF6EC]"
                >
                  Past Events
                </h2>
              </Reveal>
              <div className="mt-10 grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
                {past.map((p, i) => (
                  <PosterCard key={p.id} poster={p} index={i} onOpen={setOpen} dimmed />
                ))}
              </div>
            </>
          )}

          <Reveal>
            <div className="mx-auto mt-14 flex max-w-2xl flex-col items-center gap-4 rounded-2xl border-2 border-[#C59B4B]/70 bg-white p-8 text-center shadow-lg">
              <Info className="h-6 w-6 text-[#A61C4E]" aria-hidden="true" />
              <p className="text-base sm:text-lg leading-relaxed text-[#3D4958]">
                New dates and guest mediums are announced on our Facebook page. If you
                have a question about any service, just give us a ring — we are happy to help.
              </p>
              <a
                href={CHURCH.phoneHref}
                data-testid="events-call-button"
                className="inline-flex min-h-12 items-center gap-2 rounded-xl bg-[#C59B4B] px-6 text-base font-semibold text-[#101826] shadow-md transition-all duration-200 hover:bg-[#d4ab5f] active:scale-95"
              >
                <Phone className="h-4 w-4" aria-hidden="true" />
                {CHURCH.phoneDisplay}
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      <Lightbox poster={open} onClose={() => setOpen(null)} />
    </>
  );
}
